import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import { DateRange } from "react-day-picker";
import {
  useDailyRevenueByCustomer,
  CustomerRevenueData,
} from "@/api/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PieChart, Pie, Cell } from "recharts";

interface Props {
  dateRange: DateRange;
}

// Define types for the transformed data
interface CustomerRevenueSummary {
  id: string;
  name: string;
  revenue: number;
}

interface DailyChartDataItem {
  date: string;
  formattedDate: string;
  total: number;
  [key: string]: string | number;
}

interface PieChartDataItem {
  name: string;
  value: number;
  id: string;
}

// Helper function to get an array of unique customer IDs from data
const getUniqueCustomers = (
  data: CustomerRevenueData[] | undefined | null,
  limit = 5
): CustomerRevenueSummary[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Group by customer and calculate total revenue
  const customerRevenues = data.reduce<Record<string, CustomerRevenueSummary>>(
    (acc, curr) => {
      const customerId = curr.customerId ?? "unknown";
      if (!acc[customerId]) {
        acc[customerId] = {
          id: customerId,
          name: curr.customerName,
          revenue: 0,
        };
      }
      acc[customerId].revenue += curr.revenue;
      return acc;
    },
    {}
  );

  // Sort by revenue and take top N
  return Object.values(customerRevenues)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit);
};

// Transform data for the daily chart
const transformDataForDaily = (
  data: CustomerRevenueData[] | undefined | null,
  topCustomers: CustomerRevenueSummary[]
): DailyChartDataItem[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Get set of top customer IDs for filtering
  const topCustomerIds = new Set(topCustomers.map((c) => c.id));

  // Group by date
  const groupedByDate = data.reduce<Record<string, DailyChartDataItem>>(
    (acc, curr) => {
      const dateStr = curr.date;
      if (!acc[dateStr]) {
        acc[dateStr] = {
          date: dateStr,
          formattedDate: format(parseISO(dateStr), "MMM d"),
          total: 0,
        };
      }

      // Add to total revenue for this date
      acc[dateStr].total += curr.revenue;

      // Only include top customers in the chart
      const customerId = curr.customerId ?? "unknown";
      if (topCustomerIds.has(customerId)) {
        acc[dateStr][customerId] = curr.revenue;
        acc[dateStr][`${customerId}_name`] = curr.customerName;
      }

      return acc;
    },
    {}
  );

  // Convert to array and sort by date
  return Object.values(groupedByDate).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
};

// Transform data for the pie chart
const transformDataForPie = (
  topCustomers: CustomerRevenueSummary[]
): PieChartDataItem[] => {
  return topCustomers.map((customer) => ({
    name: customer.name,
    value: customer.revenue,
    id: customer.id,
  }));
};

// Colors for the charts
const CHART_COLORS = [
  "#4f46e5", // indigo
  "#0ea5e9", // sky
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
];

// Custom tooltip component for bar chart
const CustomBarTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  if (active && payload?.length) {
    return (
      <Card className="p-2 bg-white shadow-md border">
        <CardContent className="p-2">
          <p className="font-bold">{label}</p>
          {payload.map((entry, index) => {
            // Cast to access properties safely
            const dataKey = entry.dataKey as string | undefined;

            // Handle special case for total
            if (dataKey === "total") {
              return (
                <div key={`total-${index}`} className="flex justify-between">
                  <span style={{ color: entry.color }}>Total Revenue: </span>
                  <span className="font-semibold ml-2">
                    ${Number(entry.value).toFixed(2)}
                  </span>
                </div>
              );
            }

            // For customer revenues
            const customerNameKey = `${dataKey}_name`;
            const customerName =
              typeof entry.payload === "object" &&
              entry.payload !== null &&
              customerNameKey in entry.payload
                ? String(
                    (entry.payload as Record<string, unknown>)[customerNameKey]
                  )
                : "Unknown Customer";

            return (
              <div key={`customer-${index}`} className="flex justify-between">
                <span style={{ color: entry.color }}>{customerName}: </span>
                <span className="font-semibold ml-2">
                  ${Number(entry.value).toFixed(2)}
                </span>
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  }
  return null;
};

// Custom tooltip component for pie chart
const CustomPieTooltip = ({
  active,
  payload,
}: TooltipProps<number, string>) => {
  if (active && payload?.length) {
    const data = payload[0];
    return (
      <Card className="p-2 bg-white shadow-md border">
        <CardContent className="p-2">
          <p className="font-bold">{data.name}</p>
          <div className="flex justify-between">
            <span style={{ color: data.color }}>Revenue: </span>
            <span className="font-semibold ml-2">
              ${Number(data.value).toFixed(2)}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }
  return null;
};

export default function RevenueByCustomer({ dateRange }: Props) {
  const [activeTab, setActiveTab] = useState("daily");

  // Use the API hook
  const { data, isLoading, error } = useDailyRevenueByCustomer(dateRange);

  // Get top customers by revenue
  const topCustomers = useMemo(() => getUniqueCustomers(data), [data]);

  // Calculate total revenue in the period
  const totalRevenue = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0;
    return data.reduce((sum, item) => sum + item.revenue, 0);
  }, [data]);

  // Format for currency display
  const formattedTotalRevenue = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(totalRevenue);
  }, [totalRevenue]);

  // Transform data for both chart types
  const dailyChartData = useMemo(
    () => transformDataForDaily(data, topCustomers),
    [data, topCustomers]
  );
  const pieChartData = useMemo(
    () => transformDataForPie(topCustomers),
    [topCustomers]
  );

  if (isLoading) {
    return (
      <div className="w-full h-[400px] flex items-center justify-center">
        <Skeleton className="w-full h-[400px]" />
      </div>
    );
  }

  if (error) {
    console.error("Error fetching revenue data:", error);
    return (
      <div className="w-full h-[400px] flex items-center justify-center text-red-500">
        Failed to fetch data. Please try again.
      </div>
    );
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-full h-[400px] flex items-center justify-center text-gray-500">
        No data available for the selected date range
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center p-3 bg-muted rounded">
        <span className="font-medium">Total Revenue</span>
        <span className="text-lg font-bold">{formattedTotalRevenue}</span>
      </div>

      <Tabs
        defaultValue="daily"
        className="w-full"
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="daily">Daily Revenue</TabsTrigger>
          <TabsTrigger value="distribution">Customer Distribution</TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-4">
          <div className="w-full h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dailyChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formattedDate" />
                <YAxis />
                <Tooltip content={<CustomBarTooltip />} />
                <Legend />
                <Bar
                  dataKey="total"
                  fill="#4f46e5"
                  name="Total Revenue"
                  radius={[4, 4, 0, 0]}
                />
                {topCustomers.slice(0, 3).map((customer, index) => (
                  <Bar
                    key={customer.id}
                    dataKey={customer.id}
                    name={customer.name}
                    fill={CHART_COLORS[index + 1]}
                    radius={[4, 4, 0, 0]}
                    stackId="customer"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>
        <TabsContent value="distribution" className="mt-4">
          <div className="w-full h-[400px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={150}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ cx, cy, midAngle, outerRadius, value, name }) => {
                    const RADIAN = Math.PI / 180;
                    const radius = outerRadius * 1.1;
                    const x = (cx +
                      radius * Math.cos(-midAngle * RADIAN)) as number;
                    const y = (cy +
                      radius * Math.sin(-midAngle * RADIAN)) as number;

                    return (
                      <text
                        x={x}
                        y={y}
                        fill="#333"
                        textAnchor={x > cx ? "start" : "end"}
                        dominantBaseline="central"
                        className="text-xs"
                      >
                        {name} (${Number(value).toFixed(2)})
                      </text>
                    );
                  }}
                >
                  {pieChartData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-4">
        <h3 className="text-sm font-medium mb-2">Top Revenue by Customer</h3>
        <div className="grid grid-cols-1 gap-2">
          {topCustomers.map((customer) => (
            <div
              key={customer.id}
              className="flex justify-between items-center p-2 bg-muted rounded"
            >
              <span className="text-sm">{customer.name}</span>
              <span className="text-sm font-semibold">
                ${customer.revenue.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
